/* eslint-disable unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars, no-unused-vars */
import { Fragment, type ReactNode } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';
import deepEqual from 'react-fast-compare';
import type { PriorityFlightTextKeys } from '../../../types/labs/priorityFlight/PriorityFlightTextKeys';
import { EmptyType } from '../../../types/labs/priorityFlight/PriorityFlightTextKeys';
import commonConfig from '../../libs/configs/commonConfig';
import type { WidgetTextKeys } from '../../../types/manageMyBooking/WidgetTextKeys';
import htmlParse from 'html-react-parser';
import type {
  CarrierCode,
  CityCode,
  Fee,
  OrderStatusString,
  Tax,
  Variant,
  PurchaseModeString,
  Equipment,
  CabinType,
  NumberBetween0_9,
  UpgradeTypeString,
} from '../../../types/CoreShoppingExperience';
import type Country from '../../../types/payment/Country';
import type Language from '../../../types/Language';
import type Currency from '../../../types/payment/Currency';
import type { TicketingReasonCodeString } from '../../../types/CoreShoppingExperience/Submittable';
import type { DiscountTypeTextKey } from '../../../types/offers/Discount';
import { renderDebugTextKey, renderStyledDebugTextKey } from './utils/textKeysDebug';
import {
  type TextKeyOverride,
  fetchTextkeyOverrides,
  overrideTextkeysTransformer,
} from './utils/fetchTextkeyOverrides';
import {
  EmptyTypeEligibilityWidget,
  type EligibilityWidgetTextKeys,
} from '../../../types/eligibilityWidget/EligibilityWidgetTextKeys';
import {
  EmptyTypeGlobalMarketingPage,
  type GlobalMarketingPageTextKeys,
} from '../../../types/globalMarketingPage/GlobalMarketingPageTextKeys';
import detectHtml from './utils/detectHtml';
import Constants from './utils/constants';
import splitByArgument from './utils/splitByArgument';
import { EmptyTypeUpsell, type UpsellTextKeys } from '../../../types/airline/upsell/UpsellTextKeys';
import mapLanguageCodeForTranslations from '../../libs/utils/languages/mapLanguageCodeForTranslations';
import pseudoLocalize from './utils/pseudoLocalize';
import type { SeatStatusString } from '../../../types/DynamicSeatBlocker/SeatMap/Seatmap';

export interface TextKey {
  code: string;
  text: string;
  pid: string;
}

export interface RawTextKeyResponse {
  collectionId: string;
  messages: TextKey[];
}

export interface Translation {
  initialized: boolean;
  loading: boolean;
  error: boolean;
  textKeys: Record<string, string>;
  pid: string;
  suppressed: boolean;
  pseudoLocalized: boolean;
}

export type Translate = (code: TextKeyCode, ...args: (string | number | React.ReactNode)[]) => string;

const initialState: Translation = {
  initialized: false,
  loading: false,
  textKeys: {},
  pid: '',
  error: false,
  suppressed: false,
  pseudoLocalized: false,
};

const translation = makeVar<Translation>(initialState);

const setTranslation = (newTextKeys: Partial<Translation>) => {
  const currentTextkeys = translation();
  if (!deepEqual(currentTextkeys, newTextKeys)) {
    translation({
      ...currentTextkeys,
      ...newTextKeys,
    });
  }
};

export const UNSAFE_setTranslation = setTranslation;

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const UNSAFE_resetTranslation = () => {
  translation(initialState);
};

const toggleSuppression = () => {
  const previous = translation();
  translation({
    ...previous,
    suppressed: !previous.suppressed,
  });
};

const togglePseudoLocalization = () => {
  const previous = translation();
  translation({
    ...previous,
    pseudoLocalized: !previous.pseudoLocalized,
  });
};

export const UNSAFE_renderPseudoLocalizedText = (text: string, ...args: Array<string | number>) => {
  text = pseudoLocalize(text);
  const result = args.length > 0 ? replacePlaceholders(text, args) : text;
  return renderHtml(result);
};

export const UNSAFE_renderStyledPseudoLocalizedText = (text: string, ...args: Array<React.ReactNode>) => {
  text = pseudoLocalize(text);
  const result = args.length > 0 ? replaceStyledPlaceholders(text, args) : text;
  return result as string;
};

const transformTextKey = (
  textKeyResponse: RawTextKeyResponse,
  pid: string,
  textkeyOverrides: Array<TextKeyOverride>,
) => {
  const transformedTextkeys = Object.fromEntries(
    textKeyResponse.messages.map((item) => [`${item.code}_${item.pid}`, item.text]),
  );
  return overrideTextkeysTransformer(transformedTextkeys, pid, textkeyOverrides);
};

export const fetchTextkeys = async (pid: string, lang: string, requestId?: string, channelSource?: string) => {
  try {
    setTranslation({ loading: true, pid });
    const languageCode = mapLanguageCodeForTranslations(lang);

    const fetchTextkeyOverridesTask =
      requestId && requestId.length > 0 ? [fetchTextkeyOverrides(pid, requestId, channelSource)] : [];

    const [fetchedTextkeys, textkeyOverrides] = await Promise.all([
      fetch(`${commonConfig.staticBaseUrl}/textkeys/${pid}/${languageCode}/${pid}-${languageCode}-minimal.json`),
      ...fetchTextkeyOverridesTask,
    ]);

    const textKeyResult = (await fetchedTextkeys.json()) as RawTextKeyResponse;
    // we only support overriding non cross ailine textkeys for now
    const transformedTextKeys = transformTextKey(textKeyResult, pid, textkeyOverrides);
    setTranslation({
      initialized: true,
      loading: false,
      error: false,
      textKeys: transformedTextKeys,
    });
  } catch (error) {
    console.error(error);
    setTranslation({ loading: false, error: true });
  }
};

const findTextKey = (code: string, pid: string) => {
  const pidToUse = pid || translation().pid;
  const foundTranslation =
    translation().textKeys[`${code}_${pidToUse}`] ?? translation().textKeys[`${code}_${Constants.DEFAULT_PID}`];
  return typeof foundTranslation === 'string' ? foundTranslation : code;
};

const replacePlaceholders = (text: string, args: Array<string | number>): string =>
  text.replace(/{(\d+)}/g, (placeholder, index) => args[index].toString() ?? placeholder);

const renderHtml = (result: string) => (detectHtml(result) ? (htmlParse(result) as string) : result);
const renderIfNecessary = (arg: ReactNode | string) => (typeof arg === 'string' ? renderHtml(arg) : arg);
const replaceStyledPlaceholders = (text: string, args: Array<ReactNode | string>) => (
  <>
    {splitByArgument(text).map((part, index) => (
      // eslint-disable-next-line react/no-array-index-key -- it is the only option there
      <Fragment key={index}>{typeof part === 'number' ? renderIfNecessary(args[part]) : renderHtml(part)}</Fragment>
    ))}
  </>
);

const translate: Translate = (code: TextKeyCode, text: string, ...args: Array<string | number>) => {
  // if key suppression is active, return only the code with no translations + list of args joined by a ;
  if (translation().suppressed) {
    return renderDebugTextKey(code, args);
  }
  if (translation().pseudoLocalized) {
    text = pseudoLocalize(text);
  }

  const result = args.length > 0 ? replacePlaceholders(text, args) : text;
  return renderHtml(result);
};

const translateWithJsxArgs: Translate = (code: TextKeyCode, text: string, ...args: Array<React.ReactNode>) => {
  // if key suppression is active, return only the code with no translations + list of args joined by a ;
  if (translation().suppressed) {
    return renderStyledDebugTextKey(code, args);
  }
  if (translation().pseudoLocalized) {
    text = pseudoLocalize(text);
  }

  const result = args.length > 0 ? replaceStyledPlaceholders(text, args) : text;
  return result as string;
};

const translateTextKeys = (code: TextKeyCode, text: string, ...args: Array<string | number | React.ReactNode>) => {
  if (args.every((arg) => typeof arg === 'string' || typeof arg === 'number')) {
    return translate(code, text, ...(args as string[]));
  }
  return translateWithJsxArgs(code, text, ...args);
};

const t: Translate = (code: TextKeyCode, ...args: Array<string | number | React.ReactNode>) => {
  const text = findTextKey(code, '');
  return translateTextKeys(code, text, ...args);
};

// This for cross airline or in the furture it can be used by affiliate program
const tc: Translate = (code: TextKeyCode, pid: string, ...args: Array<string | number>) => {
  const text = findTextKey(code, pid);
  return translateTextKeys(code, text, ...args);
};

const useTranslation = () => {
  const reactiveTranslation = useReactiveVar(translation);

  return {
    actions: {
      t,
      tc,
      toggleSuppression,
      togglePseudoLocalization,
      fetchTextkeys,
    },
    states: reactiveTranslation,
  };
};
export type IUseSetSessionContent = ReturnType<typeof useTranslation>;

export default useTranslation;

/**
 * Hacky way:
 * The file '/types/labs/priorityFlight/PriorityFlightTextKeys.ts' isn't recognized as a module by TypeScript
 * and is not picked up by 'dts-loader'. Hence, there are no corresponding types for it under 'canvas_states' in .wp_federation.
 * Usually, using import and export statements should suffice for TypeScript to acknowledge it as a module.
 * The workaround: EmptyType, an enum defined in from '/types/labs/priorityFlight/PriorityFlightTextKeys.ts' which is imported in this file
 * and forces TypeScript to treat this file as a module.
 * Note: Simply importing the enum might not be adequate due to TypeScript's optimization strategy,
 * which defers type generation until it detects actual usage or reference within the codebase.
 * Hence why, we are "using" the `notUsed` variable
 */
const notUsed = EmptyType;
const notUsedEligibilityWidget = EmptyTypeEligibilityWidget;
const notUsedGlobalMarketingPage = EmptyTypeGlobalMarketingPage;
const notUsedUpsell = EmptyTypeUpsell;
export interface AllKeys
  extends TextKeys,
    PriorityFlightTextKeys,
    WidgetTextKeys,
    EligibilityWidgetTextKeys,
    GlobalMarketingPageTextKeys,
    UpsellTextKeys {}
export type TextKeyCode = keyof Record<keyof AllKeys, string>;

// dts-loader doesn't work well with external types leave this at the end to make it work
/**
 * all textkeys should be defined following the pattern:
 * [key]: string // [<folders they are used>] // <default english translation>
 */
export interface TextKeys {
  'default.page.title': string; // Get Upgraded
  'default.page.description': string; // Get Upgraded
  'offer.hero.title.color-text': string; // Choose flight upgrades
  'offer.hero.title.dark-text': string; // for added space and comfort for your upcoming travel
  'offer.header.menu': string; // Menu
  'offer.hero.guest': string; // Guests
  'offer.global.disclaimer': string; // *We will not refund any ancillary charges or fees associated with our products or services. All transactions are considered final. Do you need more information? View more details on Cabin Upgrades
  'offer.hero.accordion.how-works': string; // How it works
  'offer.hero.accordion.item-1': string; // 1. Select your bid amount per guest for each of your flights by moving the slider or instant purchasing the upgrade of your choice (subject to availability)
  'offer.hero.accordion.item-2': string; // 2. Bid on multiple upgrades to maximize your chance to be upgraded
  'offer.hero.accordion.item-3': string; // 3. Submit your offers, you will be charged only if you win (one upgrade per flight)
  'offer.hero.accordion.item-1.onboard-upgrade': string; // 1. Enter your payment details and click submit
  'offer.hero.accordion.item-2.onboard-upgrade': string; // 2. You will receive an email that confirms your upgrade has been processed successfully
  'offer.hero.accordion.item-3.onboard-upgrade': string; // 3. Press the call button above your seat and show the flight attendant your confirmation page. They will escort you to your upgraded seat
  'offer.page.title': string; // Your Bids
  'offer.trip-info.operated-by-partner': string; // Operated by {0}
  'offer.trip-accordion.see-all-offers': string; // See all offers
  'offer.trip-accordion.hide-offers': string; // Hide offers
  'offer.trip-accordion.your-offers': string; // Your offers
  'offer.trip-accordion.upgrade-offers-singular': string; // Upgrade to {0}
  'offer.trip-accordion.upgrade-offers-plural': string; // {0} offers available
  'offer.trip-accordion.purchase-mode-for-offers': string; // {0} for {1}
  'offer.trip-accordion.review-offers': string; // Review offers
  'offer.product.number': string; // {0} {1}
  'offer.product.number.operated.by': string; // {0} - Operated by {1}
  'offer.trip.connection.description': string; // Transit time / Connecting flight
  'offer.bid-card.show-benefits': string; // Show benefits
  'offer.bid-card.make-offer-in': string; // Make offer in
  'offer.bid-card.offer-in.points': string; // pts
  'offer.bid-card.make-offer-in.CASH': string; // Cash
  'offer.bid-card.make-offer-in.POINTS': string; // Points
  'offer.bid-card.or': string; // or
  [key: `offer.bid-card.footnote.text.${Variant['textKey']}`]: string; // Passengers travelling with pets are not eligible to purchase an upgrade to Business Class
  'offer.bid-card.ask-confirmation.description': string; // Instant purchase of {0} will replace your bids for the following flight.
  'offer.bid-card.ask-confirmation.description-instant-upgrade': string; // Instant purchase of {0} will replace your instant purchase of {1} for the following flight.
  'offer.bid-card.upgrade.instant.description': string; // Buy now for {0} per passenger({1})
  'offer.bid-card.upgrade.instant.button': string; // Buy now
  'offer.bid-card.upgrade.bid.button': string; // Bid now
  'offer.bid-card.upgrade.instant.button.active': string; // Selected
  'offer.bid-card.upgrade.eligible': string; // Not eligible for upgrade
  'offer.bid-card.upgrade.button': string; // Place offer
  'offer.bid-card.upgrade.button.update': string; // Update
  'offer.bid-card.upgrade.button.modify': string; // Update offer
  'offer.bid-card.upgrade.button.remove': string; // Remove offer
  'offer.bid-card.upgrade.cancel.button': string; // Cancel offer
  'offer.bid-card.upgrade.cancel.button.modify': string; // Cancel
  'offer.bid-card.upgrade.cancel.button.undo': string; // Undo
  'offer.bid-card.snackbar.cancel': string; // Offer of {0} has been cancelled
  'offer.bid-card.snackbar.add': string; // Offer of {0} has been added
  'offer.bid-card.snackbar.remove': string; // Offer of {0} has been removed
  'offer.bid-card.snackbar.update': string; // Offer updated to {0}
  'offer.bid-card.snackbar.add-instant-upgrade': string; // {0} to {1} {2} upgrade has been added
  'offer.bid-card.snackbar.remove-instant-upgrade': string; // {0} to {1} {2} upgrade has been removed
  'offer.bid-card.snackbar.mixed-currency-update': string; // Offer modified
  'offer.bid-card.upgrade.mixed-currency.description': string; // Offer placed
  'offer.bid-card.upgrade.instant.desc': string; // Get upgraded instantly without bidding for
  'offer.bid-card.upgrade.instant.desc-x': string; // Get upgraded instantly without bidding for {0}
  'offer.bid-card.upgrade.instant.boost-offer': string; // Boost
  'offer.bid-card.confirm-new-bid': string; // Yes
  'offer.bid-card.cancel-new-bid': string; // Cancel
  'offer.bid-card-action.loading': string; // Looking for the best price
  'offer.bid-card-action.no-availability': string; // Sorry, no availability
  'offer.bid-card-action.place-bid': string; // Place your bid
  'offer.bid-card-action.bid-amount': string; // Your bid amount
  'offer.bid-card-action.bid-amount-total': string; // Total bid for this:
  'offer.bid-card-action.modify-amount': string; // Modify your bid
  'offer.bid-card-action.modify-amount-note': string; // You will be only charged if your bid is accepted.
  'offer.bid-card-action.per-guest': string; // Per guest (x...)
  'offer.bid-card-action.offer-strength.no-offer': string; // 'No offer'
  'offer.bid-card-action.offer-strength.poor': string; // Poor
  'offer.bid-card-action.offer-strength.fair': string; // Fair
  'offer.bid-card-action.offer-strength.good': string; // Good
  'offer.bid-card-action.offer-strength.strong': string; // Strong
  'offer.bid-card-action.offer-strength.excellent': string; // Excellent
  'offer.bid-card-action.keep-bid': string; // Keep my current bid
  'offer.bid-card-action.modify': string; // Modify
  'offer.bid-card-action.remove': string; // Remove
  'offer.bid-card-action.checkout': string; // Checkout
  'offer.bid-card-action.mixed-currency.description': string; // Place your offer using cash & points
  'offer.bid-card.upgrade.not-eligible': string; // Not eligible for upgrade
  [key: `offer.bid-card-action.${DiscountTypeTextKey}`]: string; // The bid price reflects a {0}% discount to normal bid pricing. Valid until {1}.
  [key: `offer.bid-card-action.${DiscountTypeTextKey}-discount-label`]: string; // {0}% Discount
  [key: `offer.bid-card.${Variant['textKey']}.title`]: string; // [cruise] // Courtyard Penthouse with Balcony
  [key: `offer.bid-card.${Variant['textKey']}.description`]: string; // Beautiful dining and sitting areas, spacious rooms, and amazing balconies make these penthouses unforgettable. Be the last to watch the sun dip into the horizon in your Aft-Facing Penthouse.
  [key: `offer.bid-card.${Variant['textKey']}.details`]: string; // [cruise] // Choose a luxurious Mini-Suite with balcony which is substantially larger than a Balcony stateroom and receive a complimentary welcome glass of bubbly. Mini-Suites include a separate sitting area with sofa bed and two flat-panel televisions. For families or groups needing a little extra space, Mini-Suites offer an appealing and affordable option.
  [key: `offer.${Variant['textKey']}.cabin-capacity`]: string; // [cruise] // Up to 4 guests
  [key: `offer.${Variant['textKey']}.cabin-deck`]: string; // [cruise] // Deck 15
  [key: `offer.${Variant['textKey']}.cabin-location`]: string; // [cruise] // Middle
  [key: `offer.bid-card.action.${PurchaseModeString}.title`]: string; // Exclusive offer!
  [key: `offer.bid-card.action.${PurchaseModeString}.description`]: string; // Don't miss out on this, get upgraded today for only
  [key: `offer.bid-card.action.secondary.${PurchaseModeString}.title`]: string; // Exclusive offer!
  [key: `offer.bid-card.action.secondary.${PurchaseModeString}.description`]: string; // Don't miss out on this, get upgraded today for only
  'offer.bid-card.mobile.hide-details': string; // Hide details
  'offer.bid-card.mobile.show-details': string; // Show details
  'offer.bid-card.mobile.bid-now': string; // Bid Now
  'offer.bid-card.mobile.start-from': string; // Starting from:
  'offer.bid-card.action.slider-tooltip': string; // Slide to select your bid

  'offer.bid-card.addition-taxes': string; // + taxes {0}

  'offer.cross-airline.view-offers': string; // View offers
  'offer.cross-airline.redirected-to': string; // You will be redirected to {0}
  'offer.cross-airline.operated-by': string; // This flight is operated by {0}. You will be redirected to make or modify your request.
  'offer.cross-airline.popup.title': string; // Please note
  'offer.cross-airline.popup.continue': string; // Do you wish to continue?
  'offer.cross-airline.popup.confirm': string; // Yes, continue
  'offer.cross-airline.popup.cancel': string; // No, stay on {0}
  'offer.cross-airline.banner': string; // Your itinerary contains flights operated by more than one airline. You will make your upgrade request with each airline.

  'offer.extra-seat.quick-purchase.seat.title': string; // Block a Seat
  'offer.extra-seat.quick-purchase.seat.description': string; // No one sitting next to you for only
  'offer.extra-seat.quick-purchase.row.title': string; // Block Entire Row!
  'offer.extra-seat.quick-purchase.row.description': string; // Maximum extra space for only
  'offer.extra-seat.quick-purchase.show.options': string; // View seat map and other options
  'offer.extra-seat.bid.seat.selection.offer': string; // Select the amount of empty seats you wish to place an offer. Starting from:
  'offer.extra-seat.fixed-price.seat.selection.request': string; // Select the amount of empty seats you wish to request
  'offer.extra-seat.fixed-price.seat.selection.request.add': string; // Add Request
  'offer.extra-seat.fixed-price.seat.selection.selected': string; // Selected
  'offer.extra-seat.fixed-price.seat.selection.update': string; // Update
  'offer.extra-seat.bid.change.seat.number': string;
  'offer.seatmap.extra-seat.legend.title': string; // Legend
  'offer.seatmap.extra-seat.legend.current-seats': string; // Your Selected Seat(s)
  'offer.seatmap.extra-seat.legend.extra-seats': string; // Your Extra Seat
  'offer.seatmap.extra-seat.legend.available-seats': string; // Available Seat
  'offer.seatmap.extra-seat.legend.unavailable-seats': string; // Unavailable Seat
  [key: `offer.seatmap.legend.${UpgradeTypeString}`]: string; //

  'offer.seatmap.add-to-trip': string; // Add to trip
  'offer.seatmap.how-it-works': string;
  'offer.seatmap.how-it-works-step-1': string;
  'offer.seatmap.how-it-works-step-2': string;
  'offer.seatmap.how-it-works-step-3': string;
  'offer.seatmap.seat-selection.choose-seats': string;
  'offer.seatmap.seat-selection.extra-seat': string;
  'offer.seatmap.seat-selection.select-your-seat': string;
  'offer.seatmap.dialog.back': string;
  'offer.seatmap.dialog.view': string;
  'offer.seatmap.tooltip.nonadjacent-seats': string;
  'offer.seatmap.tooltip.blockedseatonly-group': string;
  'offer.seatmap.tooltip.child-seated-alone': string;
  'offer.seatmap.tooltip.reset-seats.reason': string;
  'offer.seatmap.tooltip.no-seats-available': string;
  'offer.seatmap.seat.description': string;
  [key: `offer.seatmap.seat.${SeatStatusString}`]: string;

  'offer.seatmap.passenger-list-select-seat': string; // Select seats for

  'offer.seatmap.drawer.seat.aisle': string; //Aisle Seat
  'offer.seatmap.drawer.seat.window': string; //Window Seat
  'offer.seatmap.drawer.seat.middle': string; //Middle Seat
  'offer.seatmap.drawer.selected': string; //Selected seat
  'offer.seatmap.drawer.close': string; //Close
  'offer.seatmap.drawer.change': string; //Change main seat

  [key: `offer.${Variant['textKey']}.disclaimer.title`]: string;
  [key: `offer.${Variant['textKey']}.disclaimer.content`]: string;

  'offer.web-check-in.continue': string;
  'offer.web-check-in.continue-long': string;
  'offer.web-check-in.hero.title': string;
  'offer.web-check-in.invalid-url': string;

  'offer.booking-number': string; // Booking number
  'offer.payment-banner.selected-offers': string; // Selected offers
  'offer.payment-banner.review-pay': string; // Review and pay
  'offer.payment-banner.fallback-popup.add-to-cart': string; // Add to cart
  'offer.payment-banner.fallback-popup.no-thanks-continue': string; // No thanks, continue to payment

  'offer.passengers': string; // Passengers
  'offer.continue-payment': string; // Continue to payment
  'offer.review-payment-divider': string; // Review and Pay
  'offer.hero.title': string; // Welcome to upgrade advantage
  'offer.hero.process-explained': string; // [cruise] // Bidding process explained
  'offer.hero.call-to-action-label': string; // Start bidding
  'offer.hero.process-step-1': string; // Place a bid
  'offer.hero.process-step-2': string; // Win
  'offer.hero.process-step-3': string; // [cruise] // Upgrade
  'offer.modal.close': string; // Close
  'offer.loading.title': string; // It won't be long.
  'offer.loading.description': string; // We're checking your eligibility for a better travel experience
  'offer.hero.process-explained.content.info': string; // [cruise] // Sometimes, your cruise might not be fully booked or a cancellation occurs at the last minute. This is when your bid can give you an opportunity to cruise in a better cabin.
  'offer.hero.process-explained.content.step-1.title': string; // [cruise] // 1. Place a bid
  'offer.hero.process-explained.content.step-1.paragrah-1': string; // [cruise] // Bidding on multiple rooms will increase your chances of getting upgraded, but you'll only ever pay for one upgrade.
  'offer.hero.process-explained.content.step-1.paragrah-2': string; // [cruise] // Bid prices are based on two occupants per stateroom for the entire duration of the cruise - only the first and second guest will be charged.
  'offer.hero.process-explained.content.step-1.paragrah-3': string; // [cruise] // You will be asked to enter your payment information before submitting your bid requests.
  'offer.hero.process-explained.content.step-1.paragrah-4': string; // [cruise] // You will only be charged if your bid is accepted.
  'offer.hero.process-explained.content.warning': string; // [cruise] // You may modify or cancel your offer up until 1 day prior to departure, provided that your offer has not already been accepted by Escape Cruise Line and provided your credit card has not been charged.
  'offer.hero.process-explained.content.step-2.title': string; // [cruise] // 2. Upgrade
  'offer.hero.process-explained.content.step-2.paragrah-1': string; // [cruise] // If your bid is accepted, you’ll receive a confirmation email, and your credit card will be charged.
  'offer.hero.process-explained.content.step-2.paragrah-2': string; // [cruise] // If your bid is not accepted, don’t worry, nothing changes. Your cabin remains yours.
  'offer.hero.process-explained.content.step-2.paragrah-3': string; // [cruise] // For more answers to your questions, please visit our FAQ page.
  'offer.hero.step.0': string; // [cruise] // Bidding is a snap! Submit an offer to upgrade your stateroom in three easy steps:
  'offer.hero.step.1': string; // [cruise] // Browse through the upgrade options available to you below.
  'offer.hero.step.2': string; // [cruise] // Make your bid(s) then select “Continue.” Pro tip: Make offers on multiple rooms. It'll increase your chance of being upgraded, and you’re only charged for the one that wins!
  'offer.hero.step.3': string; // [cruise] // Add your payment information. Upon acceptance of an offer, the total amount will be charged to the credit card used to submit your offer and becomes final and non-refundable.
  'offer.hero.description': string; // [crusie] // Upgrade offers are per guest and based on double occupancy with pricing and availability subject to change. Offer amount is in addition to the cruise fare you’ve already paid. Refer to our <a href="https://www.princess.com/plan/princess-cruise-room-upgrade/#faq" target="_blank">Frequently Asked Questions</a> for details.
  'offer.modify.page.title': string; // Modify your Bid
  'offer.modify.hero.title': string; // Modify or cancel your bid
  'offer.modify.bid-card-action.cancel.confirmation.title': string; // Your Bid will be cancelled
  'offer.modify.bid-card-action.cancel.confirmation.paragraph-1': string; // Are you sure you would like to cancel your bid?
  'offer.modify.bid-card-action.cancel.confirmation.paragraph-2': string; // You will need to re-submit a new offer to be eligible for an upgrade.
  'offer.modify.bid-card-action.cancel.confirmation.submit': string; // Cancel my bid amount
  'offer.modify.bid-card-action.cancel.offer': string; // Yes, cancel my offer
  'offer.modify.bid-card-action.keep.offer': string; // No, keep my offer
  'offer.modify.bid-card-action.boost-your-bid.title': string; // [airline] // Improve your chances<br/>boost your bid
  'offer.modify.bid-card-action.boost-your-bid.button': string; // [airline] // Add {0}
  'offer.modify.bid-card-action.boost-your-bid.description': string; // [airline] // Your new order will be {0} per passenger ({1})

  'offer.modify.popup.cancelling-bid.title': string; // Your bid will be cancelled
  'offer.modify.popup.request.impossible': string; // Impossible to process your requests
  'offer.modify.no-offer': string; // No modifiable offers available
  'offer.modify.go-to-offers': string; // Go to see the offers available for me
  'offer.modify.popup.processing-bid': string; // Processing your requests
  'offer.modify.bid-card-action.modify-bid': string; // Save & Modify your bid

  'offer.modify.bid-card-action.cancel': string; // Cancel
  'offer.modify.bid-card-action.do-not-modify-my-bid': string; // Don't modify my bid
  'offer.modify.bid-card-action.bid.offer-status.label': string; // Bid {0}
  'offer.modify.bid-card-action.bid.can-modify.title': string; // You have a bid of
  'offer.modify.bid-card-action.offer-status.submitted.label': string; // Submitted
  [key: `offer.read-only.bid-card-action.description.${OrderStatusString}`]: string; // Your offer was accepted
  [key: `offer.read-only.bid-card-action.secondary.title.${OrderStatusString}`]: string; // Congratulations!
  [key: `offer.read-only.bid-card-action.secondary.description.${OrderStatusString}`]: string; // You will be upgraded on this flight.

  'offer.rtb.seats-available': string; // {0} seats available
  'offer.rtb.bidders': string; // {0} bidders
  'offer.rtb.time-left': string; // {0} left

  'offer.rtb.bid-rank.OUTBID': string; // Outbid
  'offer.rtb.bid-rank.HIGHEST': string; // Leading
  'offer.rtb.bid-rank.GENERIC': string; // Offer Submitted

  'offer.rtb.bid-card.quick-bid.title': string; // Quick bid to get in the lead
  'offer.rtb.bid-card.quick-bid.button': string; // Quick bid

  'offer.out-of-luck.title': string; // Oh no, you're out of luck
  'offer.out-of-luck.text': string; // It seems like we can't find any offers at this moment, please try again later.
  'offer.pending-offer-screen.title': string;
  'offer.pending-offer-screen.text': string;
  'offer.error.title': string; // Oh no, you're out of luck
  'offer.error.text': string; // It seems like we can't find any offers at this moment, please try again later.
  'offer.error.button.reload': string; // Reload page
  'offer.stepper.step-1': string; // Make an offer
  'offer.stepper.step-2': string; // Review and pay
  'offer.stepper.step-1.onboard-upgrade': string; // Upgrade Now
  'footer.terms-and-conditions': string; // Terms and Conditions
  'footer.back-to-top': string; // Back to top
  'footer.powered-by': string; // Powered by
  'header.language': string; // Language
  'header.currency': string; // Currency
  'header.shoppingCart': string; // Shopping cart
  'payment.page.title': string; // Payment
  'payment.form.disclaimer': string; // Should you be awarded an upgrade, your cabin location and attributes will depend on availability. RCL cannot guarantee a specific cabin number assignment within the upgraded category. If you have submitted an offer for a category with a view, your cabin's view may be obstructed. If you have Pre-Paid Gratuities you will automatically be charged the difference to cover the upgrade cost for your new category. For more information on Pre-Paid Gratuities, click here
  'payment.form.payment-error': string; // There’s been an error trying to submit your payment
  'payment.form.rejected-error': string; // An error has ocurred, please try again
  'payment.form.required': string; // Field cannot be empty
  'payment.form.required-dynamic': string; // {0} cannot be empty
  'payment.form.invalid-card-number': string; // This is not a valid card number
  'payment.form.invalid-card-number-checksum': string; // This is not a valid card number
  'payment.form.invalid-card-funding-source': string; // Debit cards are not accepted. Please enter a credit card to proceed.
  'payment.form.invalid-card-funding-source.prepaid': string; // Prepaid cards are not accepted. Please enter a credit card to proceed.
  'payment.form.invalid-card-type': string; // This type of credit card is not accepted
  'payment.form.invalid-expiration-date': string; // Invalid expiration date
  'payment.form.invalid-expiration-date.departure': string; // Your credit card expiry must be valid at the time of departure, please update your credit card details and try again
  'payment.form.invalid-cvv': string; // {0} digits required
  'payment.form.invalid-date': string; // Expired date
  'payment.form.invalid-email': string; // This is not a valid email
  'payment.form.invalid-cpf-number': string; // This is not a valid CPF number
  'payment.form.title': string; // title of the payment form
  'payment.form.note-1': string; // Enter your payment information below to continue with your request. You will only be charged if your request is accepted in the hours leading to departure.
  [key: `payment.form.note-1.${string}`]: string; // can be bid, instant or bid-and-intant
  'payment.form.cardholder.label': string; // Cardholder name
  'payment.form.card-number.label': string; // Card number
  'payment.form.firstname.placeholder': string; // Firstname
  'payment.form.lastname.placeholder': string; // Lastname
  'payment.form.expiration.label': string; // Expiration date
  'payment.form.expiration.placeholder': string; // MM / YY
  'payment.form.cvv.label': string; // CVV
  'payment.form.cvv.tooltip': string; // The CVV Number ('Card Verfication Value') on your credit card or debit card is a 3 digit number on VISAS©, Mastercard© and Discover© branded credit and debit cards. On your American Express© branded credit or debit card it is a 4 digit numeric code.
  'payment.form.cvv.tooltip-amex': string; // On your American Express© branded credit or debit card it is a 4 digit numeric code.
  'payment.form.country.label': string; // Billing address country
  'payment.form.country.selection': string; // Select your country/region
  'payment.form.country.no-results': string; // No Results
  'payment.form.postalAddress.zipCode.label': string; // Postal/Zip Code
  'payment.form.postalAddress.zipCode.placeholder': string; // ""
  'payment.form.postalAddress.street1.label': string; // Street 1
  'payment.form.postalAddress.street1.placeholder': string; // ""
  'payment.form.postalAddress.street2.label': string; // Street 2
  'payment.form.postalAddress.street2.placeholder': string; // ""
  'payment.form.postalAddress.city.label': string; // City
  'payment.form.postalAddress.city.placeholder': string; // ""
  'payment.form.postalAddress.state.label': string; // State/Province
  'payment.form.postalAddress.state.placeholder': string; // ""
  'payment.form.postalAddress.state.selection': string; // Select a state/province
  'payment.form.email.label': string; // Email
  'payment.form.cpf-number.label': string; // CPF
  'payment.form.email.placeholder': string; //
  'payment.form.fcc.label': string; // I have a Future Cruise Credit that I would like to apply towards the payment of my upgrade'
  'payment.form.terms-and-condition.accept': string; // I confirm that I accept the
  'payment.form.terms-and-condition': string; // terms and conditions
  'payment.form.terms-and-conditions-upgrade': string; // Upgrade Terms and Conditions
  'payment.form.terms-and-conditions-seating': string; // Seating Terms and Conditions
  'payment.form.submit': string; // Pay now
  'payment.form.pay-with-points-title': string; // Personal Details
  'payment.form.pay-with-points-note-1': string; // Enter your payment information below to continue with your request.
  'payment.form.extra-seat.disclaimer': string; // Your Neighbour-Free Seat(s) request is subject to availability
  'payment.form.extra-seat.round-trip-bundle': string; // Add my {0} seat to my entire journey
  'payment.form.second.checkbox': string; // On-board upgrades do not guarantee a meal from the upgraded cabin’s menu; meal selection and amenities are subject to availability and may remain limited to your original booking class
  'payment.card-decline.retry-current-card': string; // Retry the current credit card
  'payment.card-decline.retry-new-card': string; // New credit card
  'payment.card-decline.retry-current-card-ending': string; // Card ending {0}
  'payment.card-decline.could-not-process': string; // We could not process your payment
  'payment.card-decline.take-action': string; // Take action so we can still try to upgrade you
  'payment.card-decline.choose-option': string; // Choose an option
  'payment.card-decline.retry-new-card-popup.title': string; // Before continuing
  'payment.card-decline.retry-new-card-popup.description': string; // Have you contacted your bank to allow the transaction to be processed? You may need to give them a call.
  'payment.card-decline.retry-new-card-popup.confirm': string; // Yes, retry my card
  'payment.card-decline.retry-new-card-popup.cancel': string; // Cancel
  'payment.three-ds.error.title': string; // Payment Validation Error
  'payment.three-ds.error.description': string; // We were unable to process your payment at this time. Please ensure that you entered the correct card details and try again. If the issue persists, please contact your card issuer for more information.
  'payment.card-decline.modify-apply-all-offers': string; // Apply to all my bids made with card ending {0}
  'common.page.button.view-offers': string; // View Upgrades // The button visible in case of error in the cc decline flow and the user should be redirected to the offer page
  'payment.card-decline.error.no-pending-orders.title': string; // Payment update pending // title in case of errors because no pending orders in the cc decline flow
  'payment.card-decline.error.no-pending-orders.description': string; // We will attempt to process your payment shortly // description in case of errors because no pending orders in the cc decline flow
  'payment.card-decline.error.retry-credit-card.title': string; // An error occurred while processing your payment // title in case of errors while retrying the credit card in the cc decline flow
  'payment.card-decline.error.retry-credit-card.description': string; // Check that your information is correct and try again or try a new payment method // description in case of errors while retrying the credit card in the cc decline flow
  'payment.card-decline.error.new-credit-card.title': string; // An error occurred while processing your payment // title in case of errors while trying new credit card in the cc decline flow
  'payment.card-decline.error.new-credit-card.description': string; // Check that your information is correct and try again or try a new payment method // description in case of errors while trying new credit card in the cc decline flow

  'payment.card-decline.outcome.credit-card-updated.title': string; // Thank you, We will retry your payment shortly // title in case of success while updating new credit card in the cc decline flow
  'payment.card-decline.outcome.credit-card-updated.description': string; // Keep in mind there may no longer be seats available // description in case of success while updating new credit card in the cc decline flow
  'payment.card-decline.outcome.credit-card-retried.title': string; // Thank you, We will retry your payment shortly // title in case of success while retrying credit card in the cc decline flow
  'payment.card-decline.outcome.credit-card-retried.description': string; // Keep in mind there may no longer be seats available // description in case of success while retrying credit card in the cc decline flow

  'review.price-breakdown': string; // button and popup title for breakdown
  'review.trip.location': string;
  'review.trip.details': string;
  'review.ship.location': string;
  'review.ship.details': string;
  'review.amount-due-today': string;
  'review.upgrade.summary': string; // {0} {1} of {2}
  'review.nothing-to-pay-today': string; // Nothing to pay today.
  'review.charged-if-win': string; // We’ll only charge you for the offer that’s accepted. You wont be charged for more than one type of upgrade per flight.
  'review.help': string; // If you have Norwegian's Travel Protection on your current booking, you will be automatically charged to cover the upgrade cost. For more information please click here. If you have travel protection via your travel professional, please contact them to add your upgrade to your coverage.
  'review.points.insuficient-balance.banner': string; // You are using {0} points in your offers. You need to buy or earn {1} {2} points before winning.
  'review.price-breakdown.base-cost': string; // Base cost
  'review.price-breakdown.passengers': string; // Passengers
  'review.price-breakdown.passengers.quantity': string; // x{0}
  'review.price-breakdown.total': string; // Total
  'review.price-breakdown.total-bid-amount': string; // Total Bid Amount*
  'review.price-breakdown.tax-included': string; // Taxes included
  'review.price-breakdown.tax-type-included': string;
  'review.sub-total': string; // Subtotal // the amount of all the upgrades multiplied by the quantity without the taxes

  'submission.page.title': string; // Submission
  'submission.error.no-cart-items': string; // No cart items found
  'submission.error.submit-failed': string; // Failure during cart submission.
  'submission.error.submit-poll-exhausted': string; // Cart submission is taking too long.,
  'submission.error.onboard-upgrade-reboard-failed': string; // There was a problem processing your upgrade. Please contact our cabin crew for assistance for:
  [key: `submission.error.reason.${TicketingReasonCodeString}`]: string; // Unable to reboard passenger. Please reprint boarding pass.
  'submission.loading.title': string; // Offers being submitted
  'submission.loading.text': string; // Please wait while we submit your offers
  'validation.page.title': string; // Payment validation
  'validation.upgrade.number': string; // Upgrade {0} of {1}
  'validation.continue': string; // Continue validation
  'validation.warning': string; // The financial institutions require us to perform payment validation for each upgrade in your transaction.
  'confirmation.page.title': string; // Confirmation
  'confirmation.offers-submitted': string; // title in the confirmation page
  'confirmation.upgrade-confirmed': string; // Upgrade confirmed
  'confirmation.receive-by-email': string; // description in the confirmation page
  'confirmation.modify-cancel': string; // button and popup title for breakdown
  'confirmation.view-all-offers': string; // View all {0} offers
  'payment.page.review-trip': string; // title of section review trip
  'payment.page.payment-info': string; // Payment information
  'payment.page.show-order-summary': string; // Show order summary
  'payment.page.hide-order-summary': string; // Hide order summary
  'payment.page.back': string; // Back button for payment page
  'payment.page.fallback.offer': string; // Fall back offer
  [key: `common.language.${Language['code']}`]: string; // English
  [key: `common.currency.${Currency['code']}`]: string; // USD
  [key: `common.country.${Country['code']}`]: string; // USA
  'common.start-location': string; // Starts in {0}
  'common.form.required': string; // Field cannot be empty
  'common.form.required-dynamic': string; // {0} cannot be empty
  'footer.cookies': string; // Privacy Settings
  'offer.booking-card.current': string; // current
  [key: `common.city.name.${CityCode}`]: string; // city name
  [key: `common.offer.equipment.${Equipment}`]: string; // Ship Name
  [key: `common.offer.from.cabin.${Equipment}_${CabinType}`]: string; // Cabin type
  [key: `offer.${Variant['textKey']}.cabin-size`]: string; // [cruise] // Total approx size: 188 - 364 sq. ft
  [key: `offer.${Variant['textKey']}.balcony-size`]: string; // [cruise] // Balcony size: 37 - 186 sq. ft
  [key: `common.offer.upgrade.type.${Variant['textKey']}`]: string; // upgrade type
  [key: `common.airline.${CarrierCode}.name`]: string; // carrier
  'common.component.slider.bid.no-offer': string; // No offer
  'common.component.slider.all.desc': string; // Move the slider to make an offer
  'common.component.slider.modify.desc': string; // Move the slider to update your offer
  'common.trip.origin-to-destination': string; // {0} to {1}
  'offer.manage-my-booking.return': string; // Return to My Booking page

  'common.generic.error-title': string; // Something went wrong,
  'common.generic.error-text': string; // Please, try again later,
  'experience.fetch.error.title': string; // Impossible to fetch experience,

  'unsubscribe-sms.page.title': string; // Unsubscribe SMS
  'unsubscribe-sms-screen.subscribed.title': string; //Manage Your Communication Preference
  'unsubscribe-sms-screen.subscribed.text': string; // We respect your choices. If you no longer wish to receive messages from us, you can opt out easily. Click below to unsubscribe. from SMS
  'unsubscribe-sms-screen.button.text': string; // Unsubscribe From SMS
  'unsubscribe-sms-screen.unsubscribed-success.title': string; // 'Success'
  'unsubscribe-sms-screen.unsubscribed-success.text': string; // 'You have been successfully unsubscribed from SMS'
  'unsubscribe-sms-screen.unsubscribed-failed.title': string; // 'Oops! Something Went Wrong'
  'unsubscribe-sms-screen.unsubscribed-failed.text': string; // 'Please try again later.'
  'unsubscribe-sms-screen.unsubscribed-already.title': string; // 'You Are Already Unsubscribed!'
  'unsubscribe-sms-screen.unsubscribed-already.text': string; // 'If you''d like to keep receiving SMSs please contact support'

  'common.summary.multiplyBy': string; // {0} x {1}

  [key: `offer.benefits.items.${Variant['textKey']}.${NumberBetween0_9}`]: string; // benefit
  [key: `offer.benefits.${Variant['textKey']}.introduction`]: string; // Fly in Escape Air Business Class and experience comfort, luxury and style. The undeniable pleasure includes:
  [key: `offer.benefits.${Variant['textKey']}.conclusion`]: string; // Fly in <strong>Escape Air Business Class</strong> and experience comfort, luxury and style. The undeniable <a href="https://foo.ca">Learn more</a>

  [key: `common.purchase-mode.${PurchaseModeString}`]: string; // Bid or Instant Upgrade
  'common.purchase-mode.FIXED_PRICE': string; // Request

  [key: `common.tax.code.${Tax['taxCode']}.description`]: string; // help
  [key: `common.fee.name.${Fee['code']}`]: string; // Fee
  [key: `common.fee.description.${Fee['code']}`]: string; // help

  [key: `common.order.status.${OrderStatusString}`]: string; // Modifiable / Upgraded

  [key: `offer.benefits.footnote.title.${Variant['textKey']}`]: string; // COVID-19 Update
  [key: `offer.benefits.footnote.text.${Variant['textKey']}`]: string; // Escape Air continues to work diligently to ensure your journey with us remains both safe and enjoyable. Our business class inflight catering has been fully restored, however lounges at some airports may be temporarily closed.
  'marketing-page.landing.form.pnr.label': string; // Confirmation number
  'marketing-page.landing.form.pnr.placeholder': string; // Confirmation number
  'marketing-page.landing.form.last-name.label': string; // Last name
  'marketing-page.landing.form.last-name.placeholder': string; // Last name,
  'marketing-page.landing.form.invalid-pnr-length': string; // Needs to be 6 characters long
  'marketing-page.landing.form.flight-number.label': string; // Flight number
  'marketing-page.landing.form.flight-number.placeholder': string; // Flight number
  'marketing-page.landing.form.seat-number.label': string; // Seat number
  'marketing-page.landing.form.seat-number.placeholder': string; // Seat number
  'marketing-page.landing.form.seat-number.error.invalid-pattern': string; // Seat number should contain number(s) followed by 1 letter
  'marketing-page.landing.hero.title': string; // Upgrade your flight experience
  'marketing-page.landing.hero.subtitle': string; // There could be premium seats available
  'marketing-page.landing.hero.caption': string; // Discover the possibility of an upgrade! Check if you&apos;re eligible now.
  'marketing-page.landing.submit-button': string; // Check for an upgrade
  'marketing-page.landing.hero.image.alt': string; // A spacious seat deliver not only efficient, friendly and caring services, but one that is also unobtrusive, mindful of passengers need for personal space.
  'marketing-page.landing.failure.system-error': string; // Failure on Landing page - System error
  'marketing-page.landing.failure.pax-not-eligible': string; // Failure on Landing pag - Pax not eligible
  'marketing-page.landing.failure.pnr-not-found': string; // // Failure on Landing pag - PNR Not found
  'onboard-upgrade.confirmation.disclaimer.call-flight-attendant': string; // Please use the call flight attendant button to get escorted to your new seat
  'onboard-upgrade.confirmation.show-old-seat-label': string; // Old Seat
  'onboard-upgrade.confirmation.show-new-seat-label': string; // New Seat

  'offer.checkout-banner.total.label.cash': string; // Due today
  'offer.checkout-banner.continue-button': string; // Continue to checkout
  'offer.shopping-cart.image.alt': string; // Your cart is empty
  [key: `offer.shopping-cart.purchase-mode-offers.${PurchaseModeString}`]: string; // Bid for Business class
  [key: `offer.shopping-cart.purchase-mode-label.${PurchaseModeString}`]: string; // Due today
  'offer.loyalty-signin.header.button': string; // Pay with Points
  'offer.loyalty.sign-out.button': string; // Sign out
  'offer.loyalty.points-available': string; // points available
  'offer.loyalty.title': string; // {0} / {1}
  [key: `offer.loyalty.brand.${CarrierCode}`]: string; //  WestJet Rewards
  'offer.default.loyalty.redirect-login-popup.title': string; // Loyalty Sign in
  'offer.loyalty.redirect-login-popup.title': string; // {0} Sign In
  'offer.loyalty.redirect-login-popup.body': string; // You'll be redirected to {0} to sign in to your loyalty account. Once you've signed in, you'll be returned here to complete your upgrade.
  'offer.loyalty.sign-in.button': string; // Sign in
  'offer.loyalty.redirect-logout-popup.title': string; // Loyalty Sign Out
  'offer.loyalty.redirect-logout-popup.body': string; // You're signing out of your {0} account. You will be briefly redirected to {1}'s page before returning here.
  'offer.insuficient-balance-popup.title': string; // You're Almost There!
  'offer.default.insuficient-balance-popup.description1': string; // It looks like your Loyalty program balance is just {0} points shy of covering your offer.
  'offer.insuficient-balance-popup.description1': string; // It looks like your {0} program balance is just {1} points shy of covering your offer.
  'offer.insuficient-balance-popup.description2': string; // You can still place your bid!
  'offer.insuficient-balance-popup.continue': string; // Would you like to continue?
  'offer.insuficient-balance-popup.confirm': string; // Yes, continue!
  'offer.insuficient-balance-popup.cancel': string; // Change my offer
  [key: `common.loyalty.level.${number}`]: string; // Escape Air Gold

  'offer.fallback.title': string; // Fallback offer popup title
  [key: `offer.${UpgradeTypeString}.fallback.content`]: string; // Fallback offer
  'offer.upgrade.bid.title': string; // Bid Now Upgrades <-- QF only
  'offer.upgrade.bid.text1': string; // Choose the amount you’d be happy to pay and place your Bid Now Upgrade offer. <-- QF only
  'offer.upgrade.bid.text2': string; // You’ll be notified of a successful upgrade within 24 hours of your scheduled flight. <-- QF only
  'offer.upgrade.neighbour-free-seating.title': string; // Neighbour Free Seating  <-- QF only
  'offer.upgrade.title.neighbour-free-seating.text1': string; // Provide your payment details to reserve your Neighbour Free seat. <-- QF only
  'offer.upgrade.title.neighbour-free-seating.text2': string; // If we’re unable to secure your reservation, you will automatically be refunded.  <-- QF only
  'offer.trips.header.description': string; // "Day {0} - {1}",

  'web-check-in.landing.title': string; // Welcome
  'web-check-in.landing.subtitle': string; // Get ready for your flight. You can check-in or upgrade your trip below
  'web-check-in.landing.action.primary': string; // Upgrade your trip
  'web-check-in.landing.action.secondary': string; // Continue to checkin
  'web-check-in.landing.hero.image.alt': string; // A spacious seat deliver not only efficient, friendly and caring services, but one that is also unobtrusive, mindful of passengers need for personal space.
}
